import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Check, Calendar, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import CustomerSelectionModal from '../components/CustomerSelectionModal';
import EditCustomerModal from '../components/EditCustomerModal';

const CreateKittyEnrollmentStepper = ({ user }) => {
  const navigate = useNavigate();
  
  const [customers, setCustomers] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  
  // Form data
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentModeSelection, setPaymentModeSelection] = useState('');
  const [amountPerPeriod, setAmountPerPeriod] = useState('');
  const [gramsPerPeriod, setGramsPerPeriod] = useState('');
  const [rateBookingMode, setRateBookingMode] = useState('');
  const [lockedRate, setLockedRate] = useState('');
  const [notes, setNotes] = useState('');
  
  // Preview
  const [schedulePreview, setSchedulePreview] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadSchemes();
  }, []);

  useEffect(() => {
    if (selectedScheme) {
      generatePreview();
    }
  }, [selectedScheme, startDate, amountPerPeriod, gramsPerPeriod]);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadSchemes = async () => {
    try {
      const res = await axiosInstance.get('/kitty/schemes?status=Active');
      setSchemes(res.data);
    } catch (err) {
      toast.error('Error loading schemes');
    }
  };

  const handleSchemeSelect = (scheme) => {
    setSelectedScheme(scheme);
    setRateBookingMode(scheme.rate_booking_mode);
    if (scheme.payment_mode !== 'MIXED_PER_ENROLLMENT') {
      setPaymentModeSelection(scheme.payment_mode);
    }
  };

  const generatePreview = () => {
    if (!selectedScheme || !startDate) return;
    
    const preview = [];
    const start = new Date(startDate);
    const totalPeriods = selectedScheme.periods_customer_pays + selectedScheme.periods_bonus_company;
    
    for (let i = 1; i <= totalPeriods; i++) {
      const dueDate = new Date(start);
      if (selectedScheme.frequency === 'Monthly') {
        dueDate.setMonth(dueDate.getMonth() + (i - 1));
      } else if (selectedScheme.frequency === 'Weekly') {
        dueDate.setDate(dueDate.getDate() + (7 * (i - 1)));
      } else {
        dueDate.setMonth(dueDate.getMonth() + (3 * (i - 1)));
      }
      
      const isCustomerPay = i <= selectedScheme.periods_customer_pays;
      
      preview.push({
        period: i,
        dueDate: dueDate.toISOString().split('T')[0],
        type: isCustomerPay ? 'Payment' : 'Bonus',
        amount: isCustomerPay ? amountPerPeriod : (selectedScheme.bonus_rule === 'BONUS_AMOUNT_LAST_N' ? amountPerPeriod : '-'),
        grams: isCustomerPay ? gramsPerPeriod : (selectedScheme.bonus_rule === 'BONUS_GRAMS_LAST_N' ? gramsPerPeriod : '-')
      });
    }
    
    setSchedulePreview(preview);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    if (!selectedScheme) {
      toast.error('Please select a scheme');
      return;
    }
    
    const paymentMode = paymentModeSelection || selectedScheme.payment_mode;
    
    if (paymentMode === 'AMOUNT_PER_PERIOD' && !amountPerPeriod) {
      toast.error('Please enter amount per period');
      return;
    }
    
    if (paymentMode === 'GRAMS_PER_PERIOD' && !gramsPerPeriod) {
      toast.error('Please enter grams per period');
      return;
    }
    
    if (rateBookingMode === 'LOCK_AT_ENROLLMENT' && !lockedRate) {
      toast.error('Please enter locked rate');
      return;
    }

    setLoading(true);
    try {
      const enrollmentData = {
        customer_id: selectedCustomer,
        scheme_id: selectedScheme.scheme_id,
        start_date: startDate,
        payment_mode_selection: paymentModeSelection || null,
        amount_per_period: amountPerPeriod ? parseFloat(amountPerPeriod) : null,
        grams_per_period: gramsPerPeriod ? parseFloat(gramsPerPeriod) : null,
        rate_booking_mode: rateBookingMode,
        locked_rate: lockedRate ? parseFloat(lockedRate) : null,
        notes: notes || null
      };
      
      const res = await axiosInstance.post('/kitty/enrollments', enrollmentData);
      toast.success('Enrollment created successfully!');
      navigate(`/kitty/enrollments/${res.data.enrollment_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating enrollment');
    } finally {
      setLoading(false);
    }
  };

  const paymentMode = paymentModeSelection || selectedScheme?.payment_mode;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/kitty')} className="p-2 hover:bg-amber-100 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Create Kitty Enrollment</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Customer Selection */}
        <div className="premium-card border-2 border-amber-200 p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Customer *</h3>
          {selectedCustomer ? (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg px-3 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-800">
                  {customers.find(c => c.id === selectedCustomer)?.name || 'Selected'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const customer = customers.find(c => c.id === selectedCustomer);
                      setEditingCustomer(customer);
                      setShowEditCustomerModal(true);
                    }}
                    className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1"
                  >
                    <Edit3 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => setShowCustomerModal(true)}
                    className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                  >
                    Change
                  </button>
                </div>
              </div>
              {/* Customer Details */}
              <div className="space-y-1 text-xs">
                {customers.find(c => c.id === selectedCustomer)?.mobile && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mobile:</span>
                    <span className="font-semibold text-gray-800">{customers.find(c => c.id === selectedCustomer)?.mobile}</span>
                  </div>
                )}
                {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Alt Mobile:</span>
                    <span className="font-semibold text-gray-800">{customers.find(c => c.id === selectedCustomer)?.alternate_mobile}</span>
                  </div>
                )}
                {customers.find(c => c.id === selectedCustomer)?.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold text-gray-800 truncate ml-2">{customers.find(c => c.id === selectedCustomer)?.email}</span>
                  </div>
                )}
                {customers.find(c => c.id === selectedCustomer)?.address && (
                  <div className="flex flex-col gap-0.5 mt-1 pt-1 border-t border-gray-200">
                    <span className="text-gray-600">Address:</span>
                    <span className="font-semibold text-gray-800 break-words">{customers.find(c => c.id === selectedCustomer)?.address}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCustomerModal(true)}
              className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Select Customer
            </button>
          )}
        </div>

        {/* Scheme Selection */}
        <div className="premium-card border-2 border-amber-200 p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Select Scheme *</h3>
          <div className="space-y-2">
            {schemes.map(scheme => (
              <div
                key={scheme.scheme_id}
                onClick={() => handleSchemeSelect(scheme)}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedScheme?.scheme_id === scheme.scheme_id
                    ? 'border-amber-400 bg-amber-50'
                    : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-800">{scheme.name}</span>
                  {selectedScheme?.scheme_id === scheme.scheme_id && (
                    <Check className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  {scheme.periods_customer_pays} + {scheme.periods_bonus_company} • {scheme.frequency}
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedScheme && (
          <>
            {/* Start Date */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Start Date *</h3>
              <input
                type="date"
                className="input-field w-full"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* Payment Mode (if MIXED) */}
            {selectedScheme.payment_mode === 'MIXED_PER_ENROLLMENT' && (
              <div className="premium-card border-2 border-amber-200 p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Payment Mode *</h3>
                <div className="space-y-2">
                  {['AMOUNT_PER_PERIOD', 'GRAMS_PER_PERIOD'].map(mode => (
                    <label key={mode} className="flex items-center gap-2 cursor-pointer p-2 border-2 border-gray-200 rounded-lg hover:border-amber-300">
                      <input
                        type="radio"
                        name="paymentMode"
                        checked={paymentModeSelection === mode}
                        onChange={() => setPaymentModeSelection(mode)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{mode.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Amount/Grams Per Period */}
            {paymentMode === 'AMOUNT_PER_PERIOD' && (
              <div className="premium-card border-2 border-amber-200 p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Amount Per Period *</h3>
                <input
                  type="number"
                  className="input-field w-full"
                  value={amountPerPeriod}
                  onChange={(e) => setAmountPerPeriod(e.target.value)}
                  placeholder="Enter amount in ₹"
                />
              </div>
            )}

            {paymentMode === 'GRAMS_PER_PERIOD' && (
              <div className="premium-card border-2 border-amber-200 p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Grams Per Period *</h3>
                <input
                  type="number"
                  step="0.001"
                  className="input-field w-full"
                  value={gramsPerPeriod}
                  onChange={(e) => setGramsPerPeriod(e.target.value)}
                  placeholder="Enter grams"
                />
              </div>
            )}

            {/* Locked Rate (if needed) */}
            {rateBookingMode === 'LOCK_AT_ENROLLMENT' && (
              <div className="premium-card border-2 border-amber-200 p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Locked Rate (24K per gram) *</h3>
                <input
                  type="number"
                  className="input-field w-full"
                  value={lockedRate}
                  onChange={(e) => setLockedRate(e.target.value)}
                  placeholder="Enter rate in ₹"
                />
              </div>
            )}

            {/* Schedule Preview */}
            {schedulePreview.length > 0 && (
              <div className="premium-card border-2 border-amber-200 p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Schedule Preview
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100">
                      <tr>
                        <th className="px-2 py-1 text-left">#</th>
                        <th className="px-2 py-1 text-left">Due Date</th>
                        <th className="px-2 py-1 text-left">Type</th>
                        {paymentMode === 'AMOUNT_PER_PERIOD' && <th className="px-2 py-1 text-right">Amount</th>}
                        {paymentMode === 'GRAMS_PER_PERIOD' && <th className="px-2 py-1 text-right">Grams</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {schedulePreview.map((entry, idx) => (
                        <tr key={idx} className={`border-b ${entry.type === 'Bonus' ? 'bg-green-50' : ''}`}>
                          <td className="px-2 py-1">{entry.period}</td>
                          <td className="px-2 py-1">{new Date(entry.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td className="px-2 py-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                              entry.type === 'Bonus' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {entry.type}
                            </span>
                          </td>
                          {paymentMode === 'AMOUNT_PER_PERIOD' && (
                            <td className="px-2 py-1 text-right">{entry.amount ? `₹${parseFloat(entry.amount).toLocaleString('en-IN')}` : '-'}</td>
                          )}
                          {paymentMode === 'GRAMS_PER_PERIOD' && (
                            <td className="px-2 py-1 text-right">{entry.grams ? `${entry.grams}g` : '-'}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  Total Periods: {schedulePreview.length} ({selectedScheme.periods_customer_pays} payments + {selectedScheme.periods_bonus_company} bonus)
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Notes (Optional)</h3>
              <textarea
                className="input-field w-full"
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about this enrollment..."
              />
            </div>
          </>
        )}

        {/* Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 p-4 shadow-lg">
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedCustomer || !selectedScheme}
            className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Enrollment'}
          </button>
        </div>
      </div>

      {/* Customer Modal */}
      {showCustomerModal && (
        <CustomerSelectionModal
          onClose={() => setShowCustomerModal(false)}
          customers={customers}
          onCustomerSelected={(customer) => {
            if (!customers.find(c => c.id === customer.id)) {
              setCustomers([...customers, customer]);
            }
            setSelectedCustomer(customer.id || customer.customer_id);
            setShowCustomerModal(false);
            toast.success('Customer selected!');
          }}
          selectedCustomerId={selectedCustomer}
        />
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
          onCustomerUpdated={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
            loadCustomers();
          }}
        />
      )}
    </div>
  );
};

export default CreateKittyEnrollmentStepper;
